import { Box, Stack, styled } from "@mui/material";
import type { FC } from "react";
import { type DeepPartialSkipArrayKey, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { EmailFooterEditor } from "../../email-design/components/email-footer-editor";
import type { EmailDesignSchema } from "../../email-design/types/email-design-type";
import { ConditionalWrapper } from "../../generic/components/conditional-wrapper";
import { HoverBadge } from "../hover-badge";
import { ButtonsPreview } from "./button-preview";
import { ColumnsPreview } from "./columns-preview";
import { DividersPreview } from "./divider-preview";
import { HeaderPreview } from "./header-preview";
import { TextPreview } from "./text-preview";
import type { PreviewProps } from "./types";

export const WatchedLayoutPreview: FC<Omit<PreviewProps, "data">> = (props) => {
  const { control, getValues } = useFormContext<EmailDesignSchema>();
  useWatch({ control });
  const data = getValues() as DeepPartialSkipArrayKey<EmailDesignSchema> & {
    footer: EmailDesignSchema["footer"];
  };

  return <LayoutPreview {...props} data={data} />;
};

const LayoutPreview: FC<PreviewProps> = (props) => {
  const { data } = props;
  const { layout_settings } = data;

  const { t } = useTranslation();

  // The defaults are all required because styled cannot cope with undefined values
  const Paper = styled("div")`
    background-color: ${layout_settings?.paper_colour || "white"};
    padding-left: ${layout_settings?.paper_padding_side || 0}px;
    padding-right: ${layout_settings?.paper_padding_side || 0}px;
    padding-bottom: ${layout_settings?.paper_padding_bottom || 0}px;
    padding-top: ${layout_settings?.paper_padding_top || 0}px;
    max-width: ${layout_settings?.max_paper_width || 1000}px;
    margin: 0 auto;
    border: ${layout_settings?.border?.width ?? 0}px solid
      ${layout_settings?.border?.colour ?? "white"};
  `;

  return (
    <>
      <ConditionalWrapper
        condition={props.badgeProps !== undefined}
        wrapper={(children) => (
          <HoverBadge badgeContent={t("Layout")} name="layout" {...props.badgeProps}>
            {children}
          </HoverBadge>
        )}
      >
        <Box bgcolor={layout_settings?.background_colour} mt={2}>
          <HeaderPreview {...props} />
          <Paper>
            <Stack direction="column" sx={{ width: "100%" }}>
              <TextPreview {...props} />
              <DividersPreview {...props} />
              <ButtonsPreview {...props} />
              <ColumnsPreview {...props} />
            </Stack>
          </Paper>
        </Box>
      </ConditionalWrapper>
      <EmailFooterEditor data={data ?? {}} badgeProps={props.badgeProps} />
    </>
  );
};
